import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyIcon from '@mui/icons-material/Key';
import {
  createApiToken, listApiTokens, listMcpTools, mcpClientConfigs, mcpServerUrl, revokeApiToken,
} from '../../api/apiTokens.api';

export const EXPIRY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
  { value: 'never', label: 'Never' },
];

const CLIENTS = [
  { id: 'cursor', label: 'Cursor', hint: 'Add to .cursor/mcp.json (or ~/.cursor/mcp.json for every project).' },
  { id: 'claudeCode', label: 'Claude Code', hint: 'Run in a terminal.' },
  { id: 'curl', label: 'curl', hint: 'Lists the tools, to check the token works.' },
];

const day = (d) => (d ? new Date(d).toLocaleDateString() : '');

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
          } catch { /* clipboard unavailable */ }
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function Code({ children, testId }) {
  return (
    <Box
      component="pre"
      data-testid={testId}
      sx={{
        m: 0, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}
    >
      {children}
    </Box>
  );
}

function ClientConfig({ url, token }) {
  const [client, setClient] = useState('cursor');
  const configs = mcpClientConfigs(url, token || '<access token>');
  const current = CLIENTS.find((c) => c.id === client);
  return (
    <Stack spacing={1} data-testid="mcp-client-config">
      <Tabs value={client} onChange={(_e, v) => setClient(v)} sx={{ minHeight: 36 }}>
        {CLIENTS.map((c) => <Tab key={c.id} value={c.id} label={c.label} sx={{ minHeight: 36, textTransform: 'none' }} />)}
      </Tabs>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{current.hint}</Typography>
        <CopyButton text={configs[client]} label={`Copy ${current.label} setup`} />
      </Stack>
      <Code testId={`mcp-config-${client}`}>{configs[client]}</Code>
    </Stack>
  );
}

function ToolList({ tools, loading, error }) {
  if (loading) return <CircularProgress size={18} />;
  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!tools.length) {
    return (
      <Alert severity="info" data-testid="mcp-no-tools">
        No published flows yet. Publish a flow from its page and it shows up here as a tool.
        {' '}
        <Button component={RouterLink} to="/studio/flows" size="small" sx={{ textTransform: 'none' }}>Open flows</Button>
      </Alert>
    );
  }
  return (
    <Stack spacing={1} data-testid="mcp-tools">
      {tools.map((t) => (
        <Paper key={t.name} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} data-testid="mcp-tool">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" fontWeight={700}>{t.title || t.name}</Typography>
            <Chip size="small" variant="outlined" label={t.name} sx={{ fontFamily: 'monospace' }} />
            {Object.keys(t.inputSchema?.properties || {}).length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {`Inputs: ${Object.keys(t.inputSchema.properties).join(', ')}`}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t.description}</Typography>
        </Paper>
      ))}
    </Stack>
  );
}

function TokenRow({ token, onRevoke }) {
  const expired = token.expiresAt && new Date(token.expiresAt) <= new Date();
  const details = [
    `ending ${token.tokenHint}`,
    `created ${day(token.createdAt)}`,
    token.lastUsedAt ? `last used ${day(token.lastUsedAt)}` : 'never used',
    token.expiresAt ? `${expired ? 'expired' : 'expires'} ${day(token.expiresAt)}` : 'never expires',
  ];
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }} data-testid="api-token" data-token-id={token.id}>
      <Stack direction="row" spacing={1} alignItems="center">
        <KeyIcon fontSize="small" color={expired ? 'disabled' : 'primary'} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>{token.name}</Typography>
          <Typography variant="caption" color={expired ? 'error.main' : 'text.secondary'}>{details.join(' · ')}</Typography>
        </Box>
        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onRevoke(token.id)} aria-label={`Revoke ${token.name}`}>
          Revoke
        </Button>
      </Stack>
    </Paper>
  );
}

function NewToken({ onCreated }) {
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState(90);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    setBusy(true);
    setError('');
    const { token, apiToken, error: err } = await createApiToken({ name: name.trim(), expiresInDays: expiry });
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    setName('');
    onCreated(token, apiToken);
  };

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Token name"
          placeholder="Cursor on my laptop"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: 1 }}
          inputProps={{ maxLength: 80 }}
        />
        <TextField select size="small" label="Expires after" value={expiry} onChange={(e) => setExpiry(e.target.value)} sx={{ minWidth: 150 }}>
          {EXPIRY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Button variant="contained" disabled={busy || !name.trim()} onClick={create}>
          {busy ? 'Creating…' : 'Create token'}
        </Button>
      </Stack>
      {error && <Alert severity="warning" sx={{ py: 0 }}>{error}</Alert>}
    </Stack>
  );
}

/** Connect AI assistants (MCP clients) to this workspace's published flows. */
export default function AiAssistantsPanel() {
  const url = mcpServerUrl();
  const [tools, setTools] = useState({ list: [], loading: true, error: '' });
  const [tokens, setTokens] = useState([]);
  const [tokensError, setTokensError] = useState('');
  const [newToken, setNewToken] = useState('');

  const loadTokens = useCallback(async () => {
    const { tokens: list, error } = await listApiTokens();
    setTokens(list);
    setTokensError(error || '');
  }, []);

  useEffect(() => {
    listMcpTools().then(({ tools: list, error }) => setTools({ list, loading: false, error: error || '' }));
    loadTokens();
  }, [loadTokens]);

  const revoke = async (id) => {
    const { error } = await revokeApiToken(id);
    if (error) setTokensError(error);
    else setTokens((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={700}>Server address</Typography>
        <Typography variant="body2" color="text.secondary">
          AI assistants that support MCP servers over HTTP, like Cursor and Claude Code, can run your published flows.
          Each run uses your account and this workspace&apos;s connections, just like the Test tab.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Code testId="mcp-server-url">{url}</Code>
          <CopyButton text={url} label="Copy server address" />
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={700}>
          Tools your assistant will see
          {!tools.loading && !tools.error && <Chip size="small" label={tools.list.length} sx={{ ml: 1 }} />}
        </Typography>
        <ToolList tools={tools.list} loading={tools.loading} error={tools.error} />
      </Stack>

      <Stack spacing={1.5} data-testid="api-tokens">
        <Typography variant="h6" fontWeight={700}>Access tokens</Typography>
        <Typography variant="body2" color="text.secondary">
          A token lets an assistant or script act as you on flows, apps and runs. It can&apos;t sign in, change your account or create other tokens.
          Revoke a token as soon as you no longer need it.
        </Typography>
        <NewToken
          onCreated={(token, apiToken) => {
            setNewToken(token);
            setTokens((prev) => [apiToken, ...prev]);
          }}
        />
        {newToken && (
          <Alert severity="success" onClose={() => setNewToken('')} data-testid="new-api-token-alert">
            <Typography variant="body2" fontWeight={700}>Copy this token now. It won&apos;t be shown again.</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} data-testid="new-api-token">{newToken}</Typography>
              <CopyButton text={newToken} label="Copy token" />
            </Stack>
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              The setup below already includes it.
            </Typography>
          </Alert>
        )}
        {tokensError && <Alert severity="warning">{tokensError}</Alert>}
        {tokens.map((t) => <TokenRow key={t.id} token={t} onRevoke={revoke} />)}
      </Stack>

      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={700}>Set up your assistant</Typography>
        <ClientConfig url={url} token={newToken} />
      </Stack>
    </Stack>
  );
}
